import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Dialog, NavBar, Toast} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import {PhoneFill} from 'antd-mobile-icons';
import EmptyState from "../../../components/EmptyState";
import {useTranslation} from "react-i18next";
import {NativePermissions} from "../../delivery/components/PhotoNativeBridge";
import {pickupNavigationService} from "../services/PickupNavigationService";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {usePickupTaskAppointment, usePickupTaskOperations} from "../store/pickupStore";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {getBusinessDaysText, getPickupFailureReasonText} from "../../../enums/PickupEnum";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import pickedUpDataService, {PickedUpData, uploadPhotosWithTimeout} from "../../../services/PickedUpDataService";
import pickUpPhotoService, {PickUpPhotoType, PickUpPositionType} from "../services/PickUpPhotoService";
import PickUpPhotoUploadCard from '../components/PickUpPhotoUploadCard';
import PickUpPhotoViewer from '../components/PickUpPhotoViewer';
import dayjs from "dayjs";
import PickUpInfoItem from "../components/PickUpInfoItem";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";

interface MarkExceptionProps {
}

const PickupFailedPage: React.FC<MarkExceptionProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appointmentId, failedReason } = useParams<{
        appointmentId: string;
        failedReason: string;
    }>();
    // 检查是否来自地图模式
    const fromMapMode = location.state?.fromMapMode || false;
    const {t, i18n} = useTranslation();
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickUpDetail, setPickUpDetail] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);
    const {pickupTaskAppointment, loading} = usePickupTaskAppointment(appointmentId || '');
    const {handlePickupFailed} = usePickupTaskOperations();

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickUpDetail(pickupTaskAppointment);
            const currentStopNumber = pickUpDetail?.sequence;
            if (currentStopNumber && !nextItem) {
                pickupNavigationService.getNextPickupItem(currentStopNumber).then(setNextItem);
            }
        }
    }, [pickupTaskAppointment, pickUpDetail?.sequence]);


    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await pickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content: t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                pickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {
        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                        type: type as PickUpPositionType,
                        photo: photo as PickUpPhotoType
                    })),
                id: pickUpDetail?.id?.toString() || '',
                type: 'failed',
                stopNumber: pickUpDetail?.sequence?.toString(),
                channelName: pickUpDetail?.channelName,
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    // 处理揽收失败
    const handlePickupFailedMutation = async () => {
        setIsLoading(true);
        try {
            if (!photos.signFor?.objectKey || !photos.packageImg?.objectKey || !photos.warehousePhoto?.objectKey) {
                Toast.show({content: t('pages.pickUp.finished.toast.needPhotos'), position: 'center'});
                return;
            }

            const currentPhotos = {...photos};
            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: [photos?.signFor?.objectKey, photos?.packageImg?.objectKey,photos?.warehousePhoto?.objectKey],
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
            }, true);

            setPhotos(currentPhotos);
            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(
                Object.values(photos)
                    .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined)
            );

            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: photoKeys,
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
            }, false);
            
            console.log('History length:', window.history.length);
            console.log('Current pathname:', window.location.pathname);
            console.log('fromMapMode:', fromMapMode);
            if (nextItem && !fromMapMode) {
                console.log('详情下一条数据');
                navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
            } else if (fromMapMode) {
                console.log('返回到地图');
                navigateBack.navigateToLsatPage(navigate);
            } else {
                console.log('返回到列表');
                navigateBack.navigateToPickupList(navigate);
            }
        } catch (error: any) {
            if (error.networkError) {
                await savePickedUpToLocal();
                if (nextItem && !fromMapMode) {
                    navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
                } else if (fromMapMode) {
                    navigateBack.navigateToLsatPage(navigate);
                } else {
                    navigateBack.navigateToPickupList(navigate);
                }
            } else {
                console.error('Failed to complete PickupFailedPage:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.cancelledAppointmentDotUpdate'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({content: 'Mark failed, please try again', position: 'center'});
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 拨打电话
    const handleCall = () => {
        if (!pickUpDetail?.contactPhone || pickUpDetail.contactPhone.trim() === '') {
            Toast.show({
                content: t('pages.delivery.orderDetail.noPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickUpDetail.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: t('pages.delivery.orderDetail.invalidPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        try {
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: t('pages.delivery.orderDetail.phoneCallFailed', {phone: cleanPhone}),
                position: 'bottom',
                duration: 3000
            });
        }
    };

    if (!pickUpDetail) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickUpFailed.title')}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }
    const businessDaysStr = getBusinessDaysText(pickUpDetail.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.pickUpFailed.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.detailInformation')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.name')} value={pickUpDetail.channelName}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.number')}
                                  value={pickUpDetail?.sequence?.toString()}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.contact')} value={pickUpDetail?.contactName}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.phone')}
                                  value={`${pickUpDetail?.contactPhone}${pickUpDetail?.ext ? `-${pickUpDetail?.ext}` : ''}`}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.address')}
                                  value={buildAddressFromAppointment(pickUpDetail)}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.businessHours')} value={businessDaysStr}/>
                        <div className="flex justify-between py-2 border-gray-100">
                            <span className="text-gray-600 text-sm"></span>
                            <span className="text-sm font-medium text-right">{pickUpDetail?.businessHours}</span>
                        </div>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.appointmentTime')}
                                  value={formatTimestampByLanguage(pickUpDetail?.appointmentTime, i18n.language)}/>
                        <div className="flex justify-between py-2 border-gray-100">
                            <span className="text-gray-600 text-sm">{t('pages.pickUp.pickUpFailed.failedReason')}:</span>
                            <span
                                className="text-sm font-medium text-right text-[#6366f1]">{getPickupFailureReasonText(failedReason || '', i18n.language)}</span>
                        </div>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.remark')} value={pickUpDetail?.remark || '-'} />
                    </div>
                </div>

                <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.pickUpFailed.photoDesc')}
                        <span className="text-green-600 text-base">{t('pages.pickUp.pickUpFailed.photoDescDetail')}</span>
                        {t('pages.pickUp.pickUpFailed.punctuation')}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                        <PickUpPhotoUploadCard
                            photoUrl={photos.signFor?.url}
                            type="signFor"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.packageImg?.url}
                            type="packageImg"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.warehousePhoto?.url}
                            type="warehousePhoto"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>
                <div className="flex-1">
                    <Button
                        block
                        color="primary"
                        loading={isLoading}
                        disabled={!photos.signFor?.url || !photos.packageImg?.url ||!photos.warehousePhoto?.url || isLoading}
                        onClick={handlePickupFailedMutation}
                    >
                        {t('pages.pickUp.pickUpFailed.complete')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PickupFailedPage;
